/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/build/QaPhoneBuildCards.tsx
 * FILE_ID: JOBHUB-QA-PHONE-BUILD-CARDS-002
 * PURPOSE: Phone Build body as a card list: the table's data without its width.
 * WHY: Operator mockup. The table stays available behind the header toggle for
 *      anyone who wants columns; nothing was taken away.
 * LANDMINE: the row tree, checklist grouping and origin sections all come from
 *      the SAME helpers the table uses. Re-deriving them here would let the two
 *      views disagree about which section a row belongs to.
 * LANDMINE: Add checklist and per-origin adds live in the More
 *      sheet; the body keeps one small Add row. A single checklist's name and
 *      count show in the phone header, so the body does not repeat them.
 * RELATED:
 * - ./QaPhoneBuildCard.tsx (one card)
 * - ../../../../qa/useQaChecklistEditor.ts (every write)
 */
import { Fragment, useContext, useState } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import { AlertCircle, Library, Loader2, Plus } from 'lucide-react'
import type { ContextMenuItem } from '@quotech/ui'
import { originSections } from '../../../../qa/qaApiHelpers'
import { buildRowTree, type QaRowNode } from '../../../../qa/checklistTable'
import { QaInlineMenuButtons } from '../../../../qa/qaInlineMenuButtons'
import { qaRowMenuItems } from '../../../../qa/qaRowMenuItems'
import { useQaHoldTimer } from '../../../../qa/qaHoldTimer'
import { useQaChecklistEditor } from '../../../../qa/useQaChecklistEditor'
import type { QaApi, QaAreaExecution, QaItemInstance } from '../../../../qa/qaApi'
import type { JobHubHttpClient } from '../../../../jobHubDataAdapter'
import { QA_PHONE_CONTROLS } from '../qaPhoneControlInventory'
import { QaPhoneBuildCard } from './QaPhoneBuildCard'

function QaPhoneGroupHeading({ name, done, total, productNames, onHold }: {
  name: string
  done: number
  total: number
  productNames?: string[]
  onHold: () => void
}) {
  const hold = useQaHoldTimer(() => onHold())
  return (
    <h3 className="min-w-0 px-1 text-xs font-semibold text-foreground" {...hold}>
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="min-w-0 truncate">{name}</span>
        <span className="shrink-0 font-normal tabular-nums text-muted-foreground">{done}/{total}</span>
      </span>
      {(productNames?.length ?? 0) > 0 ? (
        <span className="block truncate text-[11px] font-normal text-muted-foreground" title={productNames?.join(', ')}>
          {productNames?.join(', ')}
        </span>
      ) : null}
    </h3>
  )
}

export function QaPhoneBuildCards({
  area,
  api,
  client,
  onMutated,
  disabled = false,
  checklistId = null,
  itemSelection,
  showAddRow = true,
  onUndo,
  onRedo,
  checklistMenuItems,
  onInsertFromLibrary,
}: {
  area: QaAreaExecution
  api: QaApi
  client?: JobHubHttpClient
  onMutated: () => void
  disabled?: boolean
  checklistId?: string | null
  itemSelection: {
    isSelected: (id: string, checklistId?: string, areaId?: string) => boolean
    onToggle: (id: string, checklistId?: string, areaId?: string) => void
  }
  showAddRow?: boolean
  onUndo?: () => void
  onRedo?: () => void
  checklistMenuItems?: (checklistId: string, name: string, areaId: string) => ContextMenuItem[]
  onInsertFromLibrary?: () => void
}) {
  const queryClient = useContext(QueryClientContext)
  const editor = useQaChecklistEditor({ area, api, onMutated, checklistId, queryClient })
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [menuRow, setMenuRow] = useState<QaRowNode<QaItemInstance> | null>(null)
  const [menuChecklistId, setMenuChecklistId] = useState<string | null>(null)

  /** Newly added rows open straight into their fields; an empty collapsed card
   *  reads as a bug. */
  const revealRow = (itemId: string) => {
    setExpandedId(itemId)
    requestAnimationFrame(() => {
      document.querySelector(`[data-qa-item-row="${itemId}"]`)?.scrollIntoView({ block: 'nearest' })
    })
  }

  const selectedIds = editor.items
    .filter((item) => itemSelection.isSelected(item.id, item.checklist_instance, area.id))
    .map((item) => item.id)
  const rowItems = (node: QaRowNode<QaItemInstance>) => qaRowMenuItems({
    editor,
    node,
    disabled,
    selectedIds,
    inspectionId: area.inspection_id ?? '',
    api,
    onMutated,
    setError: editor.setError,
    onUndo,
    onRedo,
  })

  const renderNodes = (
    nodes: QaRowNode<QaItemInstance>[],
    checklistNames: string[] = [],
    rowChecklistId?: string,
  ): React.ReactNode =>
    nodes.map((node) => (
      <Fragment key={node.item.id}>
        <QaPhoneBuildCard
          item={node.item}
          areaNames={area.product_names}
          checklistNames={checklistNames}
          depth={node.depth}
          expanded={expandedId === node.item.id}
          disabled={disabled}
          busy={editor.busy === `cell-${node.item.id}` || editor.busy === `hold-${node.item.id}`}
          client={client}
          selected={itemSelection.isSelected(node.item.id, rowChecklistId)}
          onToggleSelected={() => itemSelection.onToggle(node.item.id, rowChecklistId)}
          onToggleExpanded={() =>
            setExpandedId((current) => (current === node.item.id ? null : node.item.id))
          }
          onEdit={editor.editCell}
          onRowActions={() => setMenuRow(node)}
          onLongPress={() => setMenuRow(node)}
          onAddSubRow={() =>
            editor.insertRow(editor.flatIndex(node.item) + 1, {}, node.item.template_item ?? null)
          }
        />
        {menuRow && menuRow.item.id === node.item.id ? (
          <li className="list-none">
            <QaInlineMenuButtons
              items={rowItems(menuRow)}
              onDone={() => setMenuRow(null)}
            />
          </li>
        ) : null}
      </Fragment>
    ))

  return (
    <div
      data-qa-phone-control={[
        QA_PHONE_CONTROLS.buildCards,
        QA_PHONE_CONTROLS.addRow,
        QA_PHONE_CONTROLS.rowActions,
        QA_PHONE_CONTROLS.hold,
        QA_PHONE_CONTROLS.productSearch,
      ].join(' ')}
      className="space-y-3"
    >
      {editor.error && (
        <p role="alert" className="flex items-start gap-1.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {editor.error}
        </p>
      )}

      {editor.items.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          No checks yet. Add the first one below.
        </p>
      ) : (
        editor.groups.map((group) => (
          <section key={group.checklist.id} className="space-y-2">
            {checklistId ? (
              (group.checklist.product_names?.length ?? 0) > 0 ? (
                <p
                  className="truncate px-1 text-[11px] text-muted-foreground"
                  title={group.checklist.product_names?.join(', ')}
                >
                  {group.checklist.product_names?.join(', ')}
                </p>
              ) : null
            ) : (
              <>
                <QaPhoneGroupHeading
                  name={group.name}
                  done={group.done}
                  total={group.total}
                  productNames={group.checklist.product_names}
                  onHold={() => setMenuChecklistId(group.checklist.id)}
                />
                {menuChecklistId === group.checklist.id && checklistMenuItems ? (
                  <QaInlineMenuButtons
                    items={checklistMenuItems(group.checklist.id, group.name, area.id)}
                    onDone={() => setMenuChecklistId(null)}
                  />
                ) : null}
              </>
            )}
            {originSections(group.items, { flat: !!group.checklist.template_built }).map((section) => (
              <div key={`${group.checklist.id}-${section.key}`} className="space-y-2">
                {section.key !== 'all' && (
                  <label className="flex min-h-[44px] items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 shrink-0 rounded border-border"
                      aria-label={`Select all ${section.title}`}
                      checked={section.items.every((item) => itemSelection.isSelected(item.id, group.checklist.id))}
                      onChange={() => {
                        const selected = section.items.every((item) => itemSelection.isSelected(item.id, group.checklist.id))
                        for (const item of section.items) {
                          if (itemSelection.isSelected(item.id, group.checklist.id) === selected) {
                            itemSelection.onToggle(item.id, group.checklist.id)
                          }
                        }
                      }}
                    />
                    {section.title}
                  </label>
                )}
                <ul className="space-y-2">{renderNodes(buildRowTree(section.items), group.checklist.product_names, group.checklist.id)}</ul>
              </div>
            ))}
          </section>
        ))
      )}

      {showAddRow ? (
      <button
        type="button"
        data-qa-phone-control={QA_PHONE_CONTROLS.addRow}
        onClick={() => editor.addRow(revealRow)}
        disabled={disabled || editor.busy === 'add'}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        {editor.busy === 'add'
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          : <Plus className="h-3.5 w-3.5" aria-hidden />}
        Add row
      </button>
      ) : null}
      {showAddRow && checklistId && onInsertFromLibrary ? (
        <button
          type="button"
          onClick={onInsertFromLibrary}
          disabled={disabled}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <Library className="h-3.5 w-3.5" aria-hidden />
          Insert from library
        </button>
      ) : null}
    </div>
  )
}
